import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  FileText,
  Quote,
} from "lucide-react";

export default function ApplyTemplateDialog({ open, onOpenChange, template, onApplied }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [replaceContent, setReplaceContent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setProjectId("");
    setReplaceContent(false);
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || []);
      } catch (e) {
        toast.error("No se pudieron cargar tus proyectos");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const apply = async () => {
    if (!template || !projectId) {
      toast.error("Elige el proyecto donde aplicar la plantilla.");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(`/catalog/projects/${projectId}/apply`, {
        catalog_id: template.catalog_id,
        inject_chapters: true,
        replace_content: replaceContent,
      });
      toast.success(`Plantilla "${template.university_name.split("—")[0].trim()}" aplicada.`);
      onApplied?.(data);
      onOpenChange(false);
      navigate(`/dashboard/projects/${projectId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la plantilla");
    } finally {
      setApplying(false);
    }
  };

  const selectedProject = projects.find((p) => p.project_id === projectId);
  const hasExistingContent = (selectedProject?.content_text || "").trim().length > 20;

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="apply-template-dialog"
        className="sm:max-w-2xl bg-paper border-rule p-0 overflow-hidden"
      >
        <div className="px-6 pt-6 pb-4 border-b border-rule">
          <DialogHeader>
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
              {template.country}
            </p>
            <DialogTitle className="font-serif text-2xl text-ink leading-tight">
              {template.university_name}
            </DialogTitle>
            <DialogDescription className="text-ink-soft text-[13px]">
              {template.notes || "Plantilla oficial curada por unaTESIS."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-5">
          {/* Format specs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="apply-tpl-specs">
            <Spec label="Citación" value={template.citation_style} />
            <Spec label="Fuente" value={`${template.font_family} ${template.font_size_pt}pt`} />
            <Spec
              label="Márgenes"
              value={`${template.margin_top_cm}/${template.margin_bottom_cm}/${template.margin_left_cm}/${template.margin_right_cm} cm`}
              hint="T/B/L/R"
            />
            <Spec label="Interlineado" value={`${template.line_spacing}x`} />
          </div>

          {/* Chapter outline */}
          <div className="rounded-xl border border-rule bg-white p-4">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3 inline-flex items-center gap-1.5">
              <Quote size={11} /> Capitulado requerido ({template.required_chapters?.length || 0})
            </p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="apply-tpl-chapters">
              {(template.required_chapters || []).map((ch, i) => (
                <li
                  key={ch}
                  className="flex items-center gap-2 text-[12.5px] text-ink"
                  data-testid={`apply-tpl-chapter-${i}`}
                >
                  <span className="grid place-items-center h-5 w-5 rounded-full bg-forest text-paper text-[10px] shrink-0">
                    {i + 1}
                  </span>
                  <span className="font-serif">{ch}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Project picker */}
          <div className="rounded-xl border border-rule bg-white p-4">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-2 inline-flex items-center gap-1.5">
              <FileText size={11} /> Aplicar a qué proyecto
            </p>
            {loading ? (
              <p className="text-[13px] text-ink-soft italic">Cargando proyectos…</p>
            ) : projects.length === 0 ? (
              <p className="text-[13px] text-ink-soft italic">
                Aún no tienes proyectos. Crea uno primero.
              </p>
            ) : (
              <select
                data-testid="apply-tpl-project-select"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-rule bg-paper-2/30 focus:bg-white text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10"
              >
                <option value="">— Elige un proyecto —</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.title} · {p.career}
                  </option>
                ))}
              </select>
            )}

            {selectedProject && hasExistingContent && (
              <div className="mt-3 rounded-lg border border-copper/30 bg-[#FBF6EC] p-3 flex items-start gap-2">
                <AlertTriangle size={13} className="text-copper shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[12px] text-ink leading-snug">
                    Ya escribiste algo en este proyecto. Por defecto, conservaremos tu texto.
                  </p>
                  <label className="mt-1.5 flex items-center gap-2 text-[12px] text-ink-soft cursor-pointer">
                    <input
                      data-testid="apply-tpl-replace-toggle"
                      type="checkbox"
                      checked={replaceContent}
                      onChange={(e) => setReplaceContent(e.target.checked)}
                      className="accent-forest"
                    />
                    Reemplazar contenido con el capitulado de la plantilla
                  </label>
                </div>
              </div>
            )}
            {selectedProject && !hasExistingContent && (
              <p className="mt-2 text-[12px] text-forest inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} />
                Inyectaremos el capitulado en el editor automáticamente.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-rule bg-paper-2/40">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="apply-tpl-cancel"
            className="px-5 py-2.5 rounded-full text-[13px] text-ink-soft hover:bg-paper-2"
          >
            Cancelar
          </button>
          <button
            data-testid="apply-tpl-confirm"
            onClick={apply}
            disabled={applying || !projectId}
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60"
          >
            {applying ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <CheckCircle2 size={13} />
            )}
            {applying ? "Aplicando…" : "Aplicar al proyecto"}
            {!applying && (
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Spec({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-rule bg-paper-2/30 px-3 py-2">
      <p className="text-[9.5px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
        {label}
      </p>
      <p className="mt-0.5 font-serif text-[14px] text-ink leading-tight">{value}</p>
      {hint && <p className="text-[10px] text-ink-soft mt-0.5">{hint}</p>}
    </div>
  );
}
